import React, { useState } from 'react';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonInput,
  IonTextarea,
  IonGrid,
  IonRow,
  IonCol,
  IonIcon,
} from '@ionic/react';
import { addCircleOutline, trashOutline } from 'ionicons/icons';

type ArtifactEditorProps = {
  initialConfig?: any;
  onSave?: (updatedConfig: any) => void;
};

const ArtifactEditor: React.FC<ArtifactEditorProps> = ({ initialConfig, onSave }) => {
  const [config, setConfig] = useState<any>(
    initialConfig || {
      type: 'object',
      properties: {},
    }
  );

  const addField = (type: 'text' | 'image') => {
    if (type === 'image' && config.properties.image_prompt) {
      return; // Prevent adding multiple image_prompt fields
    }

    const fieldName = type === 'text' ? `new_text_field_${Date.now()}` : 'image_prompt';
    setConfig((prevConfig: any) => ({
      ...prevConfig,
      properties: {
        ...prevConfig.properties,
        [fieldName]: {
          type: type === 'text' ? 'string' : 'object',
          description: '',
          ...(type === 'image' && {
            properties: {
              image_subject: {
                type: 'string',
                description: 'Clearly define the main subject or object of the image.',
              },
              image_style: {
                type: 'string',
                description: 'Define the visual style of the image.',
              },
              image_setting: {
                type: 'string',
                description: 'Describe the background and setting of the image.',
              },
              image_mood: {
                type: 'string',
                description: 'Define the mood or tone of the image.',
              },
              image_perspective: {
                type: 'string',
                description: 'Define the angle or perspective of the image.',
              },
            },
          }),
        },
      },
    }));
  };

  const updateField = (fieldName: string, key: string, value: string) => {
    setConfig((prevConfig: any) => ({
      ...prevConfig,
      properties: {
        ...prevConfig.properties,
        [fieldName]: {
          ...prevConfig.properties[fieldName],
          [key]: value,
        },
      },
    }));
  };

  const updateImageSubField = (subField: string, value: string) => {
    setConfig((prevConfig: any) => ({
      ...prevConfig,
      properties: {
        ...prevConfig.properties,
        image_prompt: {
          ...prevConfig.properties.image_prompt,
          properties: {
            ...prevConfig.properties.image_prompt.properties,
            [subField]: {
              ...prevConfig.properties.image_prompt.properties[subField],
              description: value,
            },
          },
        },
      },
    }));
  };

  const removeField = (fieldName: string) => {
    setConfig((prevConfig: any) => {
      const newProperties = { ...prevConfig.properties };
      delete newProperties[fieldName];
      return { ...prevConfig, properties: newProperties };
    });
  };

  const handleSave = () => {
    config.required = Object.keys(config.properties);
    if (onSave) {
      onSave(config);
    }
  };

  return (
    <div style={{ margin: '0', padding: '0' }}>
        {Object.entries(config.properties).map(([fieldName, fieldConfig]: any) => (
            <IonCard style={{ margin: '0 0 16px 0', padding: '0' }}>
               <IonCardContent>
                  {fieldName !== 'image_prompt' && (
                    <IonInput
                      className='form-element'
                      labelPlacement="floating"
                      fill="outline"
                      value={fieldName}
                      placeholder="Field Name"
                      onIonChange={(e) =>
                        setConfig((prevConfig: any) => {
                          const newProperties = { ...prevConfig.properties };
                          const updatedField = newProperties[fieldName];
                          delete newProperties[fieldName];
                          return {
                            ...prevConfig,
                            properties: {
                              ...newProperties,
                              [e.detail.value!]: updatedField,
                            },
                          };
                        })
                      }
                    />
                  )}

                  {fieldConfig.type === 'string' && (
                    <IonTextarea
                      className='form-element'
                      labelPlacement="floating"
                      fill="outline"
                      rows={2}
                      value={fieldConfig.description}
                      label='Description'
                      placeholder="Description"
                      onIonChange={(e) =>
                        updateField(fieldName, 'description', e.detail.value!)
                      }
                    />
                  )}

                  {fieldConfig.type === 'object' && fieldName === 'image_prompt' && (
                    <>
                      {Object.entries(fieldConfig.properties).map(
                        ([subField, subConfig]: any) => (
                          <div key={subField} style={{ marginTop: '10px' }}>
                            <IonTextarea
                              className='form-element'
                              labelPlacement="floating"
                              fill="outline"
                              rows={2}
                              label={subField}
                              value={subConfig.description}
                              placeholder="Description"
                              onIonChange={(e) =>
                                updateImageSubField(subField, e.detail.value!)
                              }
                            />
                          </div>
                        )
                      )}
                    </>
                  )}

                  <IonButton
                    color="danger"
                    onClick={() => removeField(fieldName)}
                  >
                    <IonIcon icon={trashOutline} slot="start" />
                    Remove Field
                  </IonButton>
                </IonCardContent>
              </IonCard>
        ))}
      <IonButton onClick={handleSave} color="primary">
        Save Configuration
      </IonButton>
      <IonButton onClick={() => addField('text')} className='basic'>
        <IonIcon icon={addCircleOutline} slot="start" />
        Add Text Field
      </IonButton>
      <IonButton onClick={() => addField('image')} className='basic'>
        <IonIcon icon={addCircleOutline} slot="start" />
        Add Image Field
      </IonButton>
    </div>
  );
};

export default ArtifactEditor;
